import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkles, Eye, MapPin, Download, Footprints, Users, Share2, CloudRain, LandPlot, Bird, Camera, Droplet, Anchor, PenTool, FolderOpen, Fish, Flame, Landmark, Route, Activity, AlertTriangle } from 'lucide-react';

export type PremiumFeatureKey =
  | 'zoom_algorithmic'
  | 'satellite_map'
  | 'hiking_trails'
  | 'offline_maps'
  | 'gps_tracking'
  | 'bathing_places'
  | 'routes_gpx'
  | 'collaborative_maps'
  | 'algorithmic_spot'
  | 'precipitation_radar'
  | 'property_boundaries'
  | 'wildlife_layer'
  | 'webcams_layer'
  | 'dump_stations'
  | 'coastal_layer'
  | 'drawing_tools'
  | 'content_folders'
  | 'content_sharing'
  | 'fishing_layer'
  | 'fire_danger'
  | 'discover_attractions'
  | 'svv_road_data'
  | 'svv_traffic_data'
  | 'traffic_events';

const FEATURE_CONFIG: Record<PremiumFeatureKey, {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  iconColor: string;
}> = {
  zoom_algorithmic:   { icon: Sparkles,   iconColor: 'text-emerald-400' },
  satellite_map:      { icon: Eye,        iconColor: 'text-blue-400' },
  hiking_trails:      { icon: MapPin,     iconColor: 'text-red-400' },
  offline_maps:       { icon: Download,   iconColor: 'text-emerald-400' },
  gps_tracking:       { icon: Footprints, iconColor: 'text-amber-400' },
  bathing_places:     { icon: MapPin,     iconColor: 'text-cyan-400' },
  routes_gpx:         { icon: MapPin,     iconColor: 'text-emerald-400' },
  collaborative_maps: { icon: Users,      iconColor: 'text-violet-400' },
  algorithmic_spot:   { icon: Sparkles,   iconColor: 'text-emerald-400' },
  precipitation_radar:  { icon: CloudRain, iconColor: 'text-cyan-400' },
  property_boundaries:  { icon: LandPlot,  iconColor: 'text-amber-400' },
  wildlife_layer:       { icon: Bird,      iconColor: 'text-violet-400' },
  webcams_layer:        { icon: Camera,    iconColor: 'text-cyan-400' },
  dump_stations:        { icon: Droplet,   iconColor: 'text-violet-400' },
  coastal_layer:        { icon: Anchor,    iconColor: 'text-blue-400' },
  drawing_tools:        { icon: PenTool,   iconColor: 'text-emerald-400' },
  content_folders:      { icon: FolderOpen, iconColor: 'text-amber-400' },
  content_sharing:      { icon: Share2,    iconColor: 'text-violet-400' },
  fishing_layer:        { icon: Fish,      iconColor: 'text-blue-400' },
  fire_danger:          { icon: Flame,     iconColor: 'text-red-500' },
  discover_attractions: { icon: Landmark,  iconColor: 'text-violet-400' },
  svv_road_data:        { icon: Route,     iconColor: 'text-slate-400' },
  svv_traffic_data:     { icon: Activity,  iconColor: 'text-blue-400' },
  traffic_events:       { icon: AlertTriangle, iconColor: 'text-orange-400' },
};

interface PremiumNudgeProps {
  featureKey: PremiumFeatureKey | null;
  onUpgrade: () => void;
  onDismiss: () => void;
  isGuest?: boolean;
}

const PremiumNudge: React.FC<PremiumNudgeProps> = ({
  featureKey, onUpgrade, onDismiss, isGuest = false,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (featureKey) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [featureKey]);

  if (!featureKey) return null;

  const config = FEATURE_CONFIG[featureKey];
  const Icon = config.icon;

  return (
    <div
      className={`fixed top-20 left-1/2 -translate-x-1/2 z-[3100] transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'
      }`}
    >
      <div className="bg-[var(--bg-surface)] backdrop-blur-xl border border-[var(--border-secondary)] rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 max-w-md">
        {featureKey !== 'algorithmic_spot' && (
          <div className="w-8 h-8 rounded-xl bg-[var(--bg-surface-alt)] flex items-center justify-center shrink-0">
            <Icon size={16} className={config.iconColor} />
          </div>
        )}
        <p className="text-tiny font-bold text-[var(--text-secondary)] flex-1 leading-snug">
          {t(`premium_nudge.${featureKey}`)}
        </p>
        <button
          onClick={onUpgrade}
          className="shrink-0 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-xl text-3xs font-black uppercase tracking-widest transition-all active:scale-95"
        >
          {isGuest ? t('premium_nudge.btn_login') : t('premium_nudge.btn_upgrade')}
        </button>
        <button
          onClick={onDismiss}
          className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default PremiumNudge;
